import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'

interface LocalityPoint {
    _id: string;
    category: 'TRANSPORT' | 'ESSENTIALS' | 'GROWTH_ROAD' | 'GROWTH_INFRA';
    name: string;
    condition: string;
    score: number;
    icon: string;
}

const CATEGORIES = [
    { id: 'TRANSPORT', label: 'TRANSPORT (MAX 6)', max: 6, icon: 'fa-bus' },
    { id: 'ESSENTIALS', label: 'ESSENTIALS (MAX 7)', max: 7, icon: 'fa-hospital' },
    { id: 'GROWTH_ROAD', label: 'ROAD (GROWTH)', max: 4, icon: 'fa-road' },
    { id: 'GROWTH_INFRA', label: 'INFRASTRUCTURE (GROWTH)', max: 3, icon: 'fa-helmet-safety' }
];

export default function AdminLocalityPoints() {
    const { token } = useAuth()
    const [points, setPoints] = useState<LocalityPoint[]>([])
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    // Form states
    const [editId, setEditId] = useState<string | null>(null)
    const [category, setCategory] = useState<string>('TRANSPORT')
    const [name, setName] = useState('')
    const [condition, setCondition] = useState('')
    const [score, setScore] = useState<number>(0)
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')

    useEffect(() => {
        fetchPoints()
    }, [])

    const fetchPoints = async () => {
        try {
            const res = await fetch(`${API_BASE}/locality-points`)
            const data = await res.json()
            if (Array.isArray(data)) setPoints(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImage(file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name || !condition || !token) return
        setSubmitting(true)
        setMsg(null)

        let imageUrl = preview

        try {
            if (image) {
                const fd = new FormData()
                fd.append('image', image)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) imageUrl = uploadData.url
                else throw new Error('Image upload failed')
            }

            const url = editId ? `${API_BASE}/locality-points/${editId}` : `${API_BASE}/locality-points`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ category, name, condition, score, icon: imageUrl })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Criterion updated!' : 'Criterion added!' })
                resetForm()
                setIsModalOpen(false)
                fetchPoints()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to save' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Delete this criterion?')) return
        try {
            const res = await fetch(`${API_BASE}/locality-points/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setPoints(prev => prev.filter(p => p._id !== id))
                setMsg({ type: 'success', text: 'Deleted successfully.' })
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (p: LocalityPoint) => {
        setCategory(p.category)
        setName(p.name)
        setCondition(p.condition)
        setScore(p.score)
        setPreview(p.icon)
        setImage(null)
        setEditId(p._id)
        setIsModalOpen(true)
    }

    const resetForm = () => {
        setEditId(null)
        setCategory('TRANSPORT')
        setName('')
        setCondition('')
        setScore(0)
        setImage(null)
        setPreview('')
    }

    return (
        <AdminLayout>
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-black dark:text-white mb-2 uppercase italic tracking-tight">Localities Point System</h1>
                    <p className="text-black dark:text-blue-950 font-bold uppercase tracking-widest text-[10px]">Define scoring rules for locality assessment.</p>
                </div>
                <button 
                    onClick={() => {
                        resetForm()
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-black transition-all shadow-md active:scale-95 italic text-sm"
                >
                    <i className="fa-solid fa-plus-circle"></i>
                    <span>Initialize Criterion</span>
                </button>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 font-bold uppercase tracking-widest text-xs ${
                    msg.type === 'success' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-rose-50 text-rose-700 border border-rose-100'
                }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
                    <p>{msg.text}</p>
                </div>
            )}

            <div className="space-y-8">
                {CATEGORIES.map(cat => (
                    <div key={cat.id} className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden min-h-[100px]">
                        <div className="p-6 bg-white/50 dark:bg-slate-800/50 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] flex items-center justify-center shadow-xl shadow-blue-100">
                                    <i className={`fa-solid ${cat.icon} text-base`}></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-blue-950 dark:text-white italic tracking-tight capitalize leading-none">{cat.label}</h2>
                                    <p className="text-[12px] text-blue-900 font-bold italic mt-1 leading-none">Max weightage: {cat.max} points protocol</p>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-50/50">
                                        <th className="px-6 py-4 text-left text-[11px] font-black text-blue-950 uppercase tracking-widest italic leading-none">Icon</th>
                                        <th className="px-6 py-4 text-left text-[11px] font-black text-blue-950 uppercase tracking-widest italic leading-none">Factor nomenclature</th>
                                        <th className="px-6 py-4 text-left text-[11px] font-black text-blue-950 uppercase tracking-widest italic leading-none">Logic condition</th>
                                        <th className="px-6 py-4 text-left text-[11px] font-black text-blue-950 uppercase tracking-widest italic leading-none">Score weightage</th>
                                        <th className="px-6 py-4 text-right text-[11px] font-black text-blue-950 uppercase tracking-widest italic leading-none">Executive actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {points.filter(p => p.category === cat.id).length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-blue-950 text-[11px] font-black uppercase tracking-widest">No criteria defined for this category</td>
                                        </tr>
                                    ) : (
                                        points.filter(p => p.category === cat.id).map(p => (
                                            <tr key={p._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                                <td className="px-6 py-3">
                                                    <div className="w-10 h-10 rounded-md bg-white p-2 flex items-center justify-center border border-slate-50 shadow-sm group-hover:shadow-md transition-all">
                                                        {p.icon ? (
                                                            <img src={getImageUrl(p.icon)} alt="icon" className="w-full h-full object-contain" />
                                                        ) : (
                                                            <i className="fa-solid fa-circle-nodes text-blue-900"></i>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 font-black text-blue-950 dark:text-white italic tracking-tight text-[13px] capitalize">
                                                    {p.name}
                                                </td>
                                                <td className="px-6 py-3 font-bold text-blue-950 italic text-[13px] uppercase">
                                                    {p.condition}
                                                </td>
                                                <td className="px-6 py-3">
                                                    <span className="inline-flex items-center px-4 py-1.5 rounded-md text-[10px] font-black bg-blue-50 text-blue-950 border border-blue-100 uppercase tracking-widest italic">
                                                       +{p.score} point protocol
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <div className="flex justify-end gap-3">
                                                        <button onClick={() => handleEdit(p)} className="w-9 h-9 flex items-center justify-center rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all shadow-sm active:scale-95"><i className="fa-solid fa-pen-nib text-xs"></i></button>
                                                        <button onClick={() => handleDelete(p._id)} className="w-9 h-9 flex items-center justify-center rounded-md bg-white border border-blue-900/10 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm active:scale-95"><i className="fa-solid fa-trash-can text-xs"></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#05144b]/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-md shadow-2xl overflow-hidden border border-blue-900/10 dark:border-slate-800 animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-[#05144b] text-white">
                            <div>
                                <h2 className="text-2xl font-black italic tracking-tight capitalize leading-none">{editId ? 'Modify Strategy Protocol' : 'Initialize Scoring Criterion'}</h2>
                                <p className="text-[12px] font-bold italic text-blue-100 mt-1">Configure scoring logic rules manually</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-all text-white">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <div className="p-10">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <div className="col-span-1">
                                        <label className="block text-[13px] font-black text-blue-950 italic capitalize mb-3">Classification protocol</label>
                                        <select 
                                            className="w-full px-5 py-4 rounded-md bg-white border-2 border-blue-900/10 focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] font-bold italic text-blue-950 transition-all appearance-none outline-none shadow-sm"
                                            value={category} 
                                            onChange={e => setCategory(e.target.value)}
                                        >
                                            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-[13px] font-black text-blue-950 italic capitalize mb-3">Component nomenclature</label>
                                        <input 
                                            type="text" 
                                            placeholder="e.g. Metro Terminal"
                                            className="w-full px-5 py-4 rounded-md bg-white border-2 border-blue-900/10 focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] font-bold italic text-blue-950 transition-all outline-none shadow-sm placeholder:text-blue-900"
                                            value={name} onChange={e => setName(e.target.value)} required 
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-[13px] font-black text-blue-950 italic capitalize mb-3">Logic condition metric</label>
                                        <input 
                                            type="text" 
                                            placeholder="e.g. ≤1 KM RADIUS"
                                            className="w-full px-5 py-4 rounded-md bg-white border-2 border-blue-900/10 focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] font-bold italic text-blue-950 transition-all outline-none shadow-sm placeholder:text-blue-900 uppercase"
                                            value={condition} onChange={e => setCondition(e.target.value)} required 
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
                                    <div>
                                        <label className="block text-[13px] font-black text-blue-950 italic capitalize mb-3">Point weightage score</label>
                                        <div className="relative">
                                            <input 
                                                type="number" 
                                                step="0.1"
                                                className="w-full pl-5 pr-16 py-4 rounded-md bg-white border-2 border-blue-900/10 focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] font-bold italic text-blue-950 transition-all outline-none shadow-sm"
                                                value={score} onChange={e => setScore(Number(e.target.value))} required 
                                            />
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-black text-blue-900 uppercase tracking-widest italic">WEIGHT</div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-black text-blue-950 italic capitalize mb-3">Visual asset protocol (Icon)</label>
                                        <div className="flex items-center gap-6 group">
                                            <div className="w-16 h-16 shrink-0 rounded-md border-2 border-dashed border-blue-900/10 flex items-center justify-center p-2 bg-white group-hover:border-[#05144b] transition-all overflow-hidden relative shadow-sm">
                                                {preview ? (
                                                    <img src={getImageUrl(preview)} className="max-h-full object-contain relative z-10" />
                                                ) : (
                                                    <i className="fa-solid fa-cloud-arrow-up text-blue-900"></i>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <input 
                                                    type="file" 
                                                    onChange={handleImageChange} 
                                                    className="w-full text-[12px] font-black text-blue-950 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-[#05144b] file:text-white hover:file:bg-blue-900 cursor-pointer italic transition-all" 
                                                />
                                                <p className="text-[9px] font-bold text-blue-900 uppercase mt-2 italic">SVG/PNG Optimized Assets Preferred</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6">
                                    <button 
                                        type="button" 
                                        onClick={() => setIsModalOpen(false)} 
                                        className="flex-1 py-5 text-[15px] font-black text-blue-950/50 hover:text-blue-950 transition-colors italic"
                                    >
                                        Discard
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={submitting} 
                                        className="flex-[2] py-5 rounded-md font-black italic bg-white border-2 border-[#05144b] text-[#05144b] shadow-md hover:bg-blue-50 transition-all flex items-center justify-center gap-3 active:scale-95"
                                    >
                                        {submitting ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-circle-check"></i>
                                                <span>{editId ? 'Commit Changes' : 'Publish Protocol'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


